import { useState, useEffect } from 'react';
import { useApi, apiFetch } from '../hooks/useApi';
import { uiConfirm, uiAlert } from '../utils/dialogs.js';

// ── Shared primitives ────────────────────────────────────────────────────────

function Btn({ children, onClick, variant = 'primary', size = 'md', disabled, type = 'button', style }) {
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none',
    borderRadius: 6, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1, fontFamily: 'inherit', transition: 'opacity .15s',
  };
  const sizes = { sm: { fontSize: 12, padding: '4px 10px' }, md: { fontSize: 13, padding: '7px 14px' } };
  const variants = {
    primary: { background: 'var(--pld-green)', color: '#fff' },
    ghost:   { background: 'transparent', color: 'var(--text)', border: '1px solid var(--border)' },
    danger:  { background: '#dc2626', color: '#fff' },
    success: { background: '#059669', color: '#fff' },
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      style={{ ...base, ...sizes[size], ...variants[variant], ...style }}>
      {children}
    </button>
  );
}

function Modal({ title, onClose, children, width = 600 }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onClose}>
      <div style={{ background: 'var(--surface)', borderRadius: 10, width: '100%', maxWidth: width,
        maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0,
          background: 'var(--surface)', zIndex: 1 }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20,
            cursor: 'pointer', color: 'var(--text-muted)', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '.05em' }}>
        {label}{required && <span style={{ color: '#dc2626', marginLeft: 2 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 6,
  fontSize: 13, background: 'var(--bg)', color: 'var(--text)', width: '100%', boxSizing: 'border-box',
};

function Input(props) { return <input style={inputStyle} {...props} />; }
function SelectEl({ children, ...props }) {
  return <select style={{ ...inputStyle, cursor: 'pointer' }} {...props}>{children}</select>;
}
function Textarea(props) {
  return <textarea style={{ ...inputStyle, minHeight: 80, resize: 'vertical', fontFamily: 'inherit' }} {...props} />;
}

function SectionHeader({ label, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '.07em', color: 'var(--text-muted)' }}>{label}</span>
      {action}
    </div>
  );
}

function FormSection({ title, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.07em',
        color: 'var(--pld-green)', marginBottom: 12, paddingBottom: 6,
        borderBottom: '2px solid var(--pld-green)', display: 'inline-block' }}>{title}</div>
      {children}
    </div>
  );
}

function TabBar({ tabs, active, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 2, borderBottom: '2px solid var(--border)', marginBottom: 20 }}>
      {tabs.map(t => (
        <button key={t.key} onClick={() => onChange(t.key)}
          style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600, border: 'none', background: 'none',
            cursor: 'pointer', fontFamily: 'inherit',
            color: active === t.key ? 'var(--pld-green)' : 'var(--text-muted)',
            borderBottom: active === t.key ? '2px solid var(--pld-green)' : '2px solid transparent',
            marginBottom: -2, transition: 'color .15s' }}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    draft:    { bg: '#fef3c7', color: '#92400e' },
    approved: { bg: '#d1fae5', color: '#065f46' },
    archived: { bg: '#f1f5f9', color: '#64748b' },
  };
  const s = map[status] || { bg: 'var(--border)', color: 'var(--text-muted)' };
  return (
    <span style={{ ...s, fontSize: 11, fontWeight: 700, padding: '2px 8px',
      borderRadius: 99, textTransform: 'uppercase', letterSpacing: '.05em' }}>
      {status}
    </span>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ── Spec form modal ───────────────────────────────────────────────────────────

const COMMODITY_TYPES = [
  { value: 'scotch_whisky',  label: 'Scotch Whisky' },
  { value: 'irish_whiskey',  label: 'Irish Whiskey' },
  { value: 'other_spirits',  label: 'Other Spirits' },
];

function SpecFormModal({ spec, brands, clients, spiritCategories, packageTypes, onClose, onSaved }) {
  const isEdit = !!spec;
  const { data: recipesData } = useApi('/recipes', []);
  const recipes = Array.isArray(recipesData) ? recipesData : (recipesData?.recipes ?? []);

  const [ownerType, setOwnerType] = useState(
    spec?.brand_id ? 'brand' : spec?.client_id ? 'client' : 'brand'
  );
  const [form, setForm] = useState({
    brand_id:                    spec?.brand_id ?? '',
    client_id:                   spec?.client_id ?? '',
    recipe_id:                   spec?.recipe_id ?? '',
    product_code:                spec?.product_code ?? '',
    product_name:                spec?.product_name ?? '',
    spirit_category_id:          spec?.spirit_category_id ?? '',
    commodity_type:              spec?.commodity_type ?? '',
    // Composition
    definition:                  spec?.definition ?? '',
    ingredients:                 spec?.ingredients ?? '',
    product_features:            spec?.product_features ?? '',
    raw_materials:               spec?.raw_materials ?? '',
    distillation_method:         spec?.distillation_method ?? '',
    // Technical
    abv_target:                  spec?.abv_target ?? '',
    abv_tolerance:               spec?.abv_tolerance || '0.3',
    methyl_alcohol_max_mg_per_l: spec?.methyl_alcohol_max_mg_per_l ?? '',
    density_at_20c:              spec?.density_at_20c ?? '',
    // Standard wording that suits every spirit (Joe, 4 Nov 2026: the grey
    // text wasn't being used) — filled in, so it saves unless changed.
    appearance_spec:             spec?.appearance_spec || 'Free from any sediment or extraneous matter',
    colour_spec:                 spec?.colour_spec ?? '',
    allergens:                   spec?.allergens ?? '',
    // Packaging
    package_type_id:             spec?.package_type_id ?? '',
    units_per_case:              spec?.units_per_case ?? '',
    label_placement_notes:       spec?.label_placement_notes ?? '',
    // Storage
    storage_conditions:          spec?.storage_conditions || "Room temperature, no shelf life, don't store under direct sunlight",
    // Notes
    notes:                       spec?.notes ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  // Fill in from the linked recipe (Joe, 4 Nov 2026): suggestions to tick, nothing saved until Save.
  const [suggest, setSuggest] = useState(null);
  const [pick, setPick] = useState({});
  const [suggestBusy, setSuggestBusy] = useState(false);
  async function loadSuggest() {
    setSuggestBusy(true); setErr('');
    try {
      const r = await apiFetch(`/product-specs/from-recipe/${form.recipe_id}`);
      const d = r?.data ?? r;
      setSuggest(d);
      // Ticked where there's a value and the spec's box is empty.
      setPick(Object.fromEntries(d.fields.map(f => [f.key, f.value != null && String(form[f.key] ?? '') === ''])));
    } catch (ex) { setErr(ex.message); } finally { setSuggestBusy(false); }
  }
  function applySuggest() {
    setForm(f => {
      const next = { ...f };
      for (const fl of suggest.fields) {
        if (!pick[fl.key] || fl.value == null) continue;
        next[fl.key] = fl.key === 'product_features' && f.product_features && !String(f.product_features).includes(fl.value)
          ? `${f.product_features}\n${fl.value}` : String(fl.value);
      }
      return next;
    });
    setSuggest(null);
  }

  // Start from the product (Joe, 4 Nov 2026): pick it and Blake fills in
  // what it can from the product and the recipe that makes it. On a new spec
  // everything found goes in; on an existing one only the empty boxes.
  const { data: stockData } = useApi('/stock', []);
  const products = (stockData?.stock_items ?? (Array.isArray(stockData) ? stockData : []))
    .filter(i => ['bottle_finished', 'bulk_finished'].includes(i.category) || (i.category === 'client_stock' && i.is_alcoholic));
  const [productId, setProductId] = useState('');
  const [filled, setFilled] = useState(null);
  async function pickProduct(id) {
    setProductId(id); setFilled(null); setSuggest(null);
    if (!id) return;
    setErr('');
    try {
      const r = await apiFetch(`/product-specs/from-product/${id}`);
      const d = r?.data ?? r;
      const done = []; const kept = [];
      const f = form;
      const next = { ...f };
      {
        for (const fl of d.fields) {
          if (fl.value == null) continue;
          const empty = String(f[fl.key] ?? '') === '';
          if (!isEdit || empty) { next[fl.key] = String(fl.value); done.push(fl.label); } else if (String(f[fl.key]) !== String(fl.value)) kept.push(fl.label);
        }
        if (d.recipe && (!isEdit || !f.recipe_id)) next.recipe_id = d.recipe.id;
        if (d.owner && !isEdit) { if (d.owner.type === 'client') next.client_id = d.owner.id; else next.brand_id = d.owner.id; }
      }
      setForm(next);
      if (d.owner && !isEdit) setOwnerType(d.owner.type);
      const missing = d.fields.filter(fl => fl.value == null).map(fl => fl.label);
      setFilled({ ...d, done: [...new Set(done)], kept, missing });
    } catch (ex) { setErr(ex.message); }
  }

  async function submit(e) {
    e.preventDefault();
    setSaving(true); setErr('');
    const body = {
      ...form,
      brand_id:  ownerType === 'brand'  ? (form.brand_id  || undefined) : undefined,
      client_id: ownerType === 'client' ? (form.client_id || undefined) : undefined,
      abv_target:                  form.abv_target                  ? Number(form.abv_target)  : undefined,
      abv_tolerance:               form.abv_tolerance               ? Number(form.abv_tolerance) : undefined,
      methyl_alcohol_max_mg_per_l: form.methyl_alcohol_max_mg_per_l ? Number(form.methyl_alcohol_max_mg_per_l) : undefined,
      density_at_20c:              form.density_at_20c              ? Number(form.density_at_20c) : undefined,
      units_per_case:              form.units_per_case              ? Number(form.units_per_case) : undefined,
      spirit_category_id:          form.spirit_category_id          || undefined,
      package_type_id:             form.package_type_id             || undefined,
      recipe_id:                   form.recipe_id                   || undefined,
    };
    // strip empty strings
    Object.keys(body).forEach(k => { if (body[k] === '') body[k] = undefined; });
    try {
      if (isEdit) {
        await apiFetch(`/product-specs/${spec.id}`, { method: 'PATCH', body });
      } else {
        await apiFetch('/product-specs', { method: 'POST', body });
      }
      onSaved();
    } catch (ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  return (
    <Modal title={isEdit ? `Edit Spec — ${spec.spec_ref}` : 'New Product Specification'}
      onClose={onClose} width={680}>
      <form onSubmit={submit}>

        <FormSection title="Product">
          <Field label={isEdit ? 'Fill in the empty boxes from a product' : 'Which product is this spec for?'}>
            <SelectEl value={productId} onChange={e => pickProduct(e.target.value)}>
              <option value="">{products.length ? 'Pick the product…' : 'Loading products…'}</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.name}{p.sku ? ` (${p.sku})` : ''}</option>)}
            </SelectEl>
          </Field>
          {filled && (
            <div style={{ fontSize: 12.5, lineHeight: 1.55, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 12px', marginTop: -6, marginBottom: 14 }}>
              {filled.existing && (!isEdit || filled.existing.id !== spec?.id) && (
                <div style={{ color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '6px 8px', marginBottom: 6 }}>
                  There's already a spec for this product: <strong>{filled.existing.spec_ref}</strong> ({filled.existing.product_name}).
                </div>
              )}
              <div><strong>Filled in:</strong> {filled.done.length ? filled.done.join(', ') : 'nothing new'}{filled.owner && !isEdit ? ', owner' : ''}.</div>
              <div><strong>Recipe:</strong> {filled.recipe ? <>{filled.recipe.name}{filled.bottling_recipe ? ` (the liquid in ${filled.bottling_recipe.name})` : ''}</> : 'none found that makes this product — pick one below if there is one'}.</div>
              {filled.kept.length > 0 && <div style={{ color: 'var(--text-muted)' }}>Left as they were: {filled.kept.join(', ')}.</div>}
              {filled.missing.length > 0 && <div style={{ color: 'var(--text-muted)' }}>Not found — fill in yourself: {filled.missing.join(', ')}.</div>}
              {!filled.owner && !isEdit && <div style={{ color: 'var(--text-muted)' }}>Choose the owner below — Blake couldn't tell.</div>}
              <div style={{ color: 'var(--text-muted)' }}>Check everything before saving.</div>
            </div>
          )}
        </FormSection>

        <FormSection title="Recipe">
          <Field label="Linked Recipe">
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <SelectEl value={form.recipe_id} onChange={e => { set('recipe_id')(e); setSuggest(null); }}>
                  <option value="">None — not linked to a recipe yet</option>
                  {recipes.map(r => (
                    <option key={r.id} value={r.id}>{r.name}{r.recipe_ref ? ` (${r.recipe_ref})` : ''}</option>
                  ))}
                </SelectEl>
              </div>
              <Btn variant="ghost" disabled={!form.recipe_id || suggestBusy} onClick={loadSuggest}>{suggestBusy ? 'Reading…' : 'Fill in from recipe'}</Btn>
            </div>
          </Field>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -8, marginBottom: 14 }}>
            Blake can fill in ingredients, raw materials, method, ABV and allergens from the recipe — you check them before saving. Amounts never come across.
          </div>
          {suggest && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 14, background: 'var(--surface-2)' }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>From {suggest.recipe.name}</div>
              {suggest.fields.map(f => (
                <label key={f.key} style={{ display: 'grid', gridTemplateColumns: '20px 130px 1fr', gap: 8, alignItems: 'start', fontSize: 12.5, padding: '5px 0', borderTop: '1px solid var(--border)', opacity: f.value == null ? 0.6 : 1 }}>
                  <input type="checkbox" disabled={f.value == null} checked={!!pick[f.key]} onChange={e => setPick(p => ({ ...p, [f.key]: e.target.checked }))} />
                  <span style={{ fontWeight: 600 }}>{f.label}</span>
                  <span>
                    <span style={{ whiteSpace: 'pre-line' }}>{f.value == null ? '—' : f.key === 'package_type_id' ? (packageTypes.find(p => p.id === f.value)?.name || 'Package type') : f.key === 'abv_target' ? `${f.value}%` : String(f.value)}</span>
                    <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: 11.5 }}>{f.from}{f.value != null && String(form[f.key] ?? '') !== '' && String(form[f.key]) !== String(f.value) ? ' · replaces what\'s there' : ''}</span>
                  </span>
                </label>
              ))}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                <Btn variant="ghost" size="sm" onClick={() => setSuggest(null)}>Close</Btn>
                <Btn size="sm" disabled={!Object.values(pick).some(Boolean)} onClick={applySuggest}>Fill these in</Btn>
              </div>
            </div>
          )}
        </FormSection>

        <FormSection title="Owner">
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {[{ value: 'brand', label: 'Own Brand (PLD)' }, { value: 'client', label: 'Contract Client' }].map(o => (
              <button key={o.value} type="button"
                onClick={() => setOwnerType(o.value)}
                style={{ flex: 1, padding: '8px 12px', borderRadius: 6, cursor: 'pointer',
                  fontWeight: 600, fontSize: 13, fontFamily: 'inherit', transition: 'all .15s',
                  border: ownerType === o.value ? '2px solid var(--pld-green)' : '2px solid var(--border)',
                  background: ownerType === o.value ? 'rgba(34,57,31,.06)' : 'var(--bg)',
                  color: ownerType === o.value ? 'var(--pld-green)' : 'var(--text-muted)' }}>
                {o.label}
              </button>
            ))}
          </div>
          {ownerType === 'brand' ? (
            <Field label="Brand" required>
              <SelectEl value={form.brand_id} onChange={set('brand_id')} required>
                <option value="">Select brand…</option>
                {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </SelectEl>
            </Field>
          ) : (
            <Field label="Client" required>
              <SelectEl value={form.client_id} onChange={set('client_id')} required>
                <option value="">Select client…</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </SelectEl>
            </Field>
          )}
        </FormSection>

        <FormSection title="Product Identity">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
            <Field label="Product Name" required>
              <Input value={form.product_name} onChange={set('product_name')} required />
            </Field>
            <Field label="Product Code">
              <Input value={form.product_code} onChange={set('product_code')} placeholder="e.g. PD-FPD-03" />
            </Field>
            <Field label="Spirit Category">
              <SelectEl value={form.spirit_category_id} onChange={set('spirit_category_id')}>
                <option value="">Select category…</option>
                {spiritCategories.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </SelectEl>
            </Field>
            <Field label="Commodity Type (HMRC)">
              <SelectEl value={form.commodity_type} onChange={set('commodity_type')}>
                <option value="">Select…</option>
                {COMMODITY_TYPES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </SelectEl>
            </Field>
          </div>
        </FormSection>

        <FormSection title="Composition">
          <Field label="Definition / General Type">
            <Textarea value={form.definition} onChange={set('definition')} rows={2}
              placeholder="e.g. A London dry gin with coastal/maritime influences…" />
          </Field>
          <Field label="Ingredients (full list)">
            <Textarea value={form.ingredients} onChange={set('ingredients')} rows={3}
              placeholder="e.g. Bilberry, cardamom, cassia, clove, coriander seed…" />
          </Field>
          <Field label="Product Features / Tasting Notes">
            <Textarea value={form.product_features} onChange={set('product_features')} rows={2}
              placeholder="e.g. An exceptionally smooth juniper forward maritime gin…" />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
            <Field label="Raw Materials">
              <Textarea value={form.raw_materials} onChange={set('raw_materials')} rows={2}
                placeholder="e.g. Grain neutral spirit, botanicals, R/O filtered water" />
            </Field>
            <Field label="Distillation Method">
              <Textarea value={form.distillation_method} onChange={set('distillation_method')} rows={2}
                placeholder="e.g. Pot distilled with reflux" />
            </Field>
          </div>
        </FormSection>

        <FormSection title="Technical Specification">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 14px' }}>
            <Field label="ABV Target (%)">
              <Input type="number" step="0.01" value={form.abv_target} onChange={set('abv_target')}
                placeholder="e.g. 43.00" />
            </Field>
            <Field label="ABV Tolerance (±%)">
              <Input type="number" step="0.01" value={form.abv_tolerance} onChange={set('abv_tolerance')}
                placeholder="e.g. 0.30" />
            </Field>
            <Field label="Density at 20°C (kg/m³)">
              <Input type="number" step="0.1" value={form.density_at_20c} onChange={set('density_at_20c')}
                placeholder="e.g. 946.0" />
            </Field>
            <Field label="Methyl Alcohol Max (mg/L)">
              <Input type="number" step="0.0001" value={form.methyl_alcohol_max_mg_per_l}
                onChange={set('methyl_alcohol_max_mg_per_l')} placeholder="e.g. 21.5" />
            </Field>
            <Field label="Allergens">
              <Input value={form.allergens} onChange={set('allergens')} placeholder="e.g. None — check the ingredients first" />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
            <Field label="Appearance Spec">
              <Input value={form.appearance_spec} onChange={set('appearance_spec')}
                placeholder="e.g. Free from any sediment or extraneous matter" />
            </Field>
            <Field label="Colour Spec">
              <Input value={form.colour_spec} onChange={set('colour_spec')}
                placeholder="e.g. Water-white and colourless" />
            </Field>
          </div>
        </FormSection>

        <FormSection title="Packaging">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
            <Field label="Package Type">
              <SelectEl value={form.package_type_id} onChange={set('package_type_id')}>
                <option value="">Select package type…</option>
                {packageTypes.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </SelectEl>
            </Field>
            <Field label="Units Per Case (override)">
              <Input type="number" value={form.units_per_case} onChange={set('units_per_case')}
                placeholder="Uses package type default if blank" />
            </Field>
          </div>
          <Field label="Label Placement Notes">
            <Textarea value={form.label_placement_notes} onChange={set('label_placement_notes')} rows={4}
              placeholder="e.g. Front label: 47mm from base. Back label: 50mm from base, opposite front. Duty label: 21mm from base…" />
          </Field>
        </FormSection>

        <FormSection title="Storage">
          <Field label="Storage Conditions">
            <Textarea value={form.storage_conditions} onChange={set('storage_conditions')} rows={2}
              placeholder="e.g. Room temperature, no shelf life, don't store under direct sunlight" />
          </Field>
        </FormSection>

        <FormSection title="Notes">
          <Field label="Internal Notes">
            <Textarea value={form.notes} onChange={set('notes')} rows={2} />
          </Field>
        </FormSection>

        {err && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 12 }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4,
          position: 'sticky', bottom: 0, background: 'var(--surface)', padding: '12px 0', marginBottom: -4 }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Spec'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

// ── Spec detail ───────────────────────────────────────────────────────────────

function InfoRow({ label, value, mono }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 8, padding: '8px 0',
      borderBottom: '1px solid var(--border)', fontSize: 13 }}>
      <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{label}</span>
      <span style={{ fontFamily: mono ? 'monospace' : undefined, whiteSpace: 'pre-wrap' }}>{value || '—'}</span>
    </div>
  );
}

// ── Safety data sheet (SDS / MSDS) ─────────────────────────────────────────────
// Built from the spec; this form holds the few SDS-only details, shows what
// the sheet will say (classification, transport) and downloads the PDF.
async function downloadSds(spec) {
  const res = await fetch(`/api/product-specs/${spec.id}/sds.pdf`, { headers: { Authorization: `Bearer ${localStorage.getItem('pld_token')}` } });
  if (!res.ok) { uiAlert('Safety data sheet failed to generate'); return; }
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url; a.download = `SDS ${spec.product_name}.pdf`; a.click();
  URL.revokeObjectURL(url);
}

function SdsModal({ spec, onClose }) {
  const { data: sheet, loading, refetch } = useApi(`/product-specs/${spec.id}/sds`, [spec.id]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const d = sheet?.sds || {};
  const f = form || {
    flash_point_c: d.flash_point_c ?? '', flash_point_method: d.flash_point_method || '', odour: d.odour || '',
    contact_email: d.contact_email || '', contact_phone: d.contact_phone || '',
    emergency_phone: d.emergency_phone || '', emergency_hours: d.emergency_hours || '', intended_use: d.intended_use || '',
    other_components: d.other_components || [],
  };
  const set = (k) => (e) => setForm({ ...f, [k]: e.target.value });

  // Copy the details from another spec (Joe, 4 Nov 2026). A spec with nothing
  // saved yet starts from the same client's (or brand's) latest one. The
  // measured flash point isn't copied: it belongs to that product.
  const { data: sourcesData } = useApi(`/product-specs/${spec.id}/sds-sources`, [spec.id]);
  const sources = Array.isArray(sourcesData) ? sourcesData : [];
  const [copiedFrom, setCopiedFrom] = useState(null);
  function copyFrom(src) {
    const s = src.sds || {};
    setForm({
      ...f,
      flash_point_c: f.flash_point_c, flash_point_method: f.flash_point_method,
      odour: s.odour || '', contact_email: s.contact_email || '', contact_phone: s.contact_phone || '',
      emergency_phone: s.emergency_phone || '', emergency_hours: s.emergency_hours || '', intended_use: s.intended_use || '',
      other_components: (s.other_components || []).map(c => ({ ...c })),
    });
    setCopiedFrom(src);
  }
  const hasOwn = !!sheet && ['odour', 'contact_email', 'contact_phone', 'emergency_phone', 'emergency_hours', 'intended_use', 'flash_point_c'].some(k => d[k] != null && d[k] !== '') || (d.other_components || []).length > 0;
  useEffect(() => {
    if (sheet && !hasOwn && !form && !copiedFrom && sources[0]?.same_owner) copyFrom(sources[0]);
  }, [sheet, sourcesData]); // eslint-disable-line react-hooks/exhaustive-deps

  const setComp = (i, k, v) => setForm({ ...f, other_components: f.other_components.map((c, j) => (j === i ? { ...c, [k]: v } : c)) });

  async function save(andDownload) {
    setSaving(true); setErr('');
    try {
      await apiFetch(`/product-specs/${spec.id}/sds`, { method: 'PATCH', body: { ...f, other_components: f.other_components.filter(c => c.name) } });
      setForm(null); setCopiedFrom(null); await refetch();
      if (andDownload) await downloadSds(spec);
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  }

  const t = sheet?.transport;
  const box = { background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 12, fontSize: 13, marginBottom: 14 };
  return (
    <Modal title={`Safety data sheet — ${spec.product_name}`} onClose={onClose} width={720}>
      {loading && !sheet ? <p style={{ color: 'var(--text-muted)' }}>Loading…</p> : (<>
        <div style={box}>
          <div><strong>Flash point:</strong> {sheet.flash_point_text}</div>
          <div><strong>Classification:</strong> {sheet.classification}</div>
          <div><strong>Transport:</strong> {t.summary}</div>
          <div style={{ marginTop: 6, color: 'var(--text-muted)' }}>Everything else comes from the spec (ABV, density, ingredients, appearance, allergens, bottle size, storage) and Settings (company name and address). Edit the spec to change those.</div>
        </div>
        {sheet.warnings?.length > 0 && (
          <div style={{ ...box, background: '#fffbeb', borderColor: '#fcd34d', color: '#92400e' }}>
            {sheet.warnings.map((w, i) => <div key={i}>• {w}</div>)}
          </div>
        )}
        {err && <p style={{ color: '#dc2626', fontSize: 13 }}>{err}</p>}
        {sources.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12, fontSize: 13 }}>
            {copiedFrom
              ? <span style={{ color: '#166534', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '4px 8px' }}>Filled in from <strong>{copiedFrom.product_name}</strong> — check it (especially the odour) and save.</span>
              : <span style={{ color: 'var(--text-muted)' }}>Same details as another product?</span>}
            <select value="" onChange={e => { const s = sources.find(x => x.id === e.target.value); if (s) copyFrom(s); }}
              style={{ height: 32, borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, padding: '0 8px', background: 'var(--surface)' }}>
              <option value="">{copiedFrom ? 'Copy from a different spec…' : 'Copy from another spec…'}</option>
              {sources.map(s => <option key={s.id} value={s.id}>{s.product_name}{s.owner_name ? ` — ${s.owner_name}` : ''}</option>)}
            </select>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Measured flash point (°C)"><Input type="number" step="0.1" value={f.flash_point_c} onChange={set('flash_point_c')} placeholder={`blank = calculated (${sheet.flash_point ?? '?'} °C)`} /></Field>
          <Field label="Flash point method"><Input value={f.flash_point_method} onChange={set('flash_point_method')} placeholder="e.g. Closed cup, ISO 2719" /></Field>
          <Field label="Contact email"><Input value={f.contact_email} onChange={set('contact_email')} placeholder="blank = joe@pleasantlanddistillery.com" /></Field>
          <Field label="Contact telephone"><Input value={f.contact_phone} onChange={set('contact_phone')} placeholder="blank = 07471 329575" /></Field>
          <Field label="Emergency telephone"><Input value={f.emergency_phone} onChange={set('emergency_phone')} placeholder="blank = contact telephone" /></Field>
          <Field label="Emergency line hours"><Input value={f.emergency_hours} onChange={set('emergency_hours')} placeholder="e.g. Mon–Fri 9am–5pm" /></Field>
          <Field label="Odour"><Input value={f.odour} onChange={set('odour')} placeholder="e.g. Aniseed" /></Field>
          <Field label="Intended use"><Input value={f.intended_use} onChange={set('intended_use')} placeholder="blank = Alcoholic beverage for human consumption." /></Field>
        </div>
        <div style={{ margin: '6px 0 14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <label style={{ fontSize: 13, fontWeight: 600 }}>Other components to declare <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(besides ethanol and water; optional)</span></label>
            <Btn size="sm" variant="ghost" onClick={() => setForm({ ...f, other_components: [...f.other_components, { name: '', cas: '', ec: '', percent: '', classification: '' }] })}>+ Add</Btn>
          </div>
          {f.other_components.map((c, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 0.7fr 1.6fr auto', gap: 6, marginBottom: 6 }}>
              <Input value={c.name} onChange={e => setComp(i, 'name', e.target.value)} placeholder="Name" />
              <Input value={c.cas || ''} onChange={e => setComp(i, 'cas', e.target.value)} placeholder="CAS" />
              <Input value={c.ec || ''} onChange={e => setComp(i, 'ec', e.target.value)} placeholder="EC" />
              <Input value={c.percent || ''} onChange={e => setComp(i, 'percent', e.target.value)} placeholder="%" />
              <Input value={c.classification || ''} onChange={e => setComp(i, 'classification', e.target.value)} placeholder="Classification" />
              <button type="button" onClick={() => setForm({ ...f, other_components: f.other_components.filter((_, j) => j !== i) })} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 16 }}>×</button>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>Revision {d.revision || 1}{d.revision_date ? `, ${fmtDate(d.revision_date)}` : ''} — saving a change starts a new revision.</div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" onClick={onClose}>Close</Btn>
          {form && <Btn variant="ghost" disabled={saving} onClick={() => save(false)}>Save</Btn>}
          <Btn disabled={saving} onClick={() => (form ? save(true) : downloadSds(spec))}>{saving ? 'Saving…' : form ? 'Save & download PDF' : '↓ Download PDF'}</Btn>
        </div>
      </>)}
    </Modal>
  );
}

function SpecDetail({ specId, brands, clients, spiritCategories, packageTypes, onBack, onRefresh }) {
  const { data: spec, loading, refetch } = useApi(`/product-specs/${specId}`, [specId]);
  const [editModal, setEditModal] = useState(false);
  const [approving, setApproving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [sdsOpen, setSdsOpen] = useState(false);

  async function approve() {
    if (!(await uiConfirm('Approve this specification?'))) return;
    setApproving(true);
    try {
      await apiFetch(`/product-specs/${specId}/approve`, { method: 'POST' });
      refetch();
    } catch (ex) { alert(ex.message); }
    finally { setApproving(false); }
  }

  async function archive() {
    if (!(await uiConfirm('Archive this specification? It will no longer be available for new batches.'))) return;
    setArchiving(true);
    try {
      await apiFetch(`/product-specs/${specId}/archive`, { method: 'POST' });
      refetch();
    } catch (ex) { alert(ex.message); }
    finally { setArchiving(false); }
  }

  if (loading) return <div style={{ color: 'var(--text-muted)', padding: 40, textAlign: 'center' }}>Loading…</div>;
  if (!spec) return <div style={{ color: 'var(--text-muted)', padding: 40, textAlign: 'center' }}>Spec not found</div>;

  const lpa = spec.lpa_per_unit ? `${Number(spec.lpa_per_unit).toFixed(6)} LPA` : '—';

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button onClick={onBack}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 20, padding: 0 }}>
          ←
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700,
              background: 'var(--bg)', border: '1px solid var(--border)', padding: '2px 8px', borderRadius: 4 }}>
              {spec.spec_ref}
            </span>
            <span style={{ fontWeight: 700, fontSize: 18 }}>{spec.product_name}</span>
            <StatusBadge status={spec.status} />
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
            {spec.brand_name ? `Own Brand — ${spec.brand_name}` : `Contract — ${spec.client_name}`}
            {spec.spirit_category_name && ` · ${spec.spirit_category_name}`}
            {` · Rev.${spec.revision_number}`}
            {spec.revision_date && ` (${fmtDate(spec.revision_date)})`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {spec.status === 'draft' && (
            <Btn variant="success" disabled={approving} onClick={approve}>
              {approving ? 'Approving…' : '✓ Approve'}
            </Btn>
          )}
          <Btn variant="ghost" onClick={async () => {
            const token = localStorage.getItem('pld_token');
            const res = await fetch(`/api/product-specs/${spec.id}/pdf`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) { uiAlert('PDF generation failed'); return; }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${spec.spec_ref}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
          }}>↓ PDF</Btn>
          <Btn variant="ghost" onClick={() => setSdsOpen(true)}>Safety data sheet</Btn>
          {spec.status !== 'archived' && (
            <Btn variant="ghost" onClick={() => setEditModal(true)}>Edit</Btn>
          )}
          {spec.status === 'approved' && (
            <Btn variant="ghost" disabled={archiving} onClick={archive}>
              {archiving ? '…' : 'Archive'}
            </Btn>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Left column */}
        <div>
          <SectionHeader label="Product Identity" />
          <InfoRow label="Product Code"      value={spec.product_code} mono />
          <InfoRow label="Spirit Category"   value={spec.spirit_category_name} />
          <InfoRow label="Commodity Type"    value={spec.commodity_type?.replace(/_/g, ' ')} />
          <InfoRow label="Definition"        value={spec.definition} />
          <div style={{ height: 20 }} />

          <SectionHeader label="Composition" />
          <InfoRow label="Ingredients"       value={spec.ingredients} />
          <InfoRow label="Product Features"  value={spec.product_features} />
          <InfoRow label="Raw Materials"     value={spec.raw_materials} />
          <InfoRow label="Distillation"      value={spec.distillation_method} />
          <div style={{ height: 20 }} />

          <SectionHeader label="Storage" />
          <InfoRow label="Storage Conditions" value={spec.storage_conditions} />
        </div>

        {/* Right column */}
        <div>
          <SectionHeader label="Technical Specification" />
          <InfoRow label="ABV Target"         value={spec.abv_target ? `${spec.abv_target}% ±${spec.abv_tolerance}%` : '—'} />
          <InfoRow label="Density at 20°C"    value={spec.density_at_20c ? `${spec.density_at_20c} kg/m³` : '—'} />
          <InfoRow label="Methyl Alcohol Max"  value={spec.methyl_alcohol_max_mg_per_l ? `${spec.methyl_alcohol_max_mg_per_l} mg/L` : '—'} />
          <InfoRow label="Appearance"         value={spec.appearance_spec} />
          <InfoRow label="Colour"             value={spec.colour_spec} />
          <InfoRow label="Allergens"          value={spec.allergens} />
          <div style={{ height: 20 }} />

          <SectionHeader label="Packaging" />
          <InfoRow label="Package Type"       value={spec.package_type_name} />
          {spec.bottle_model_ref && <InfoRow label="Bottle Model"     value={`${spec.bottle_manufacturer ?? ''} ${spec.bottle_model_ref}`.trim()} />}
          {spec.closure_ref      && <InfoRow label="Closure"          value={spec.closure_ref} />}
          <InfoRow label="Units Per Case"     value={spec.units_per_case ?? spec.package_units_per_case} />
          <InfoRow label="LPA Per Unit"       value={lpa} mono />
          <InfoRow label="Label Placement"    value={spec.label_placement_notes} />
          <div style={{ height: 20 }} />

          {spec.recipe_id && <>
            <SectionHeader label="Recipe" />
            <InfoRow label="Linked Recipe" value={spec.recipe_name ?? spec.recipe_id} mono />
            <div style={{ height: 20 }} />
          </>}

          <SectionHeader label="Document Control" />
          <InfoRow label="Status"             value={<StatusBadge status={spec.status} />} />
          <InfoRow label="Revision"           value={`Rev.${spec.revision_number}`} />
          <InfoRow label="Revision Date"      value={fmtDate(spec.revision_date)} />
          <InfoRow label="Approved By"        value={spec.approved_by_name} />
          <InfoRow label="Approved At"        value={fmtDate(spec.approved_at)} />
          {spec.notes && <>
            <div style={{ height: 20 }} />
            <SectionHeader label="Notes" />
            <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', color: 'var(--text-muted)', padding: '8px 0' }}>{spec.notes}</div>
          </>}
        </div>
      </div>

      {sdsOpen && <SdsModal spec={spec} onClose={() => setSdsOpen(false)} />}
      {editModal && (
        <SpecFormModal spec={spec} brands={brands} clients={clients}
          spiritCategories={spiritCategories} packageTypes={packageTypes}
          onClose={() => setEditModal(false)}
          onSaved={() => { setEditModal(false); refetch(); onRefresh(); }} />
      )}
    </div>
  );
}

// ── Spec list ─────────────────────────────────────────────────────────────────

function SpecList({ brands, clients, spiritCategories, packageTypes, onSelect }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [createModal, setCreateModal] = useState(false);

  const query = [
    search      ? `search=${encodeURIComponent(search)}` : '',
    statusFilter ? `status=${statusFilter}` : '',
    ownerFilter  ? `owner_type=${ownerFilter}` : '',
  ].filter(Boolean).join('&');

  const { data, loading, refetch } = useApi(`/product-specs${query ? '?' + query : ''}`, [search, statusFilter, ownerFilter]);
  const specs = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search specs…"
          style={{ ...inputStyle, maxWidth: 240, boxSizing: 'border-box' }} />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ ...inputStyle, maxWidth: 160, cursor: 'pointer', boxSizing: 'border-box' }}>
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="approved">Approved</option>
          <option value="archived">Archived</option>
        </select>
        <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)}
          style={{ ...inputStyle, maxWidth: 180, cursor: 'pointer', boxSizing: 'border-box' }}>
          <option value="">All specs</option>
          <option value="own_brand">Own Brand</option>
          <option value="contract">Contract</option>
        </select>
        <div style={{ flex: 1 }} />
        <Btn onClick={() => setCreateModal(true)}>+ New Spec</Btn>
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 40, textAlign: 'center' }}>Loading…</div>
      ) : specs.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 60, textAlign: 'center' }}>No specs found</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border)' }}>
              {['Ref', 'Product', 'Owner', 'Category', 'Package', 'ABV', 'Status', 'Rev.', ''].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {specs.map(s => (
              <tr key={s.id} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                onClick={() => onSelect(s.id)}>
                <td style={{ padding: '10px 10px', fontFamily: 'monospace', fontSize: 12, fontWeight: 700 }}>{s.spec_ref}</td>
                <td style={{ padding: '10px 10px', fontWeight: 600 }}>{s.product_name}</td>
                <td style={{ padding: '10px 10px', color: 'var(--text-muted)' }}>
                  {s.brand_name
                    ? <span><span style={{ fontSize: 10, background: 'rgba(34,57,31,.08)', color: 'var(--pld-green)',
                        padding: '1px 5px', borderRadius: 4, marginRight: 4, fontWeight: 700 }}>PLD</span>{s.brand_name}</span>
                    : <span><span style={{ fontSize: 10, background: '#dbeafe', color: '#1e40af',
                        padding: '1px 5px', borderRadius: 4, marginRight: 4, fontWeight: 700 }}>CONTRACT</span>{s.client_name}</span>
                  }
                </td>
                <td style={{ padding: '10px 10px', color: 'var(--text-muted)' }}>{s.spirit_category_name ?? '—'}</td>
                <td style={{ padding: '10px 10px', color: 'var(--text-muted)' }}>{s.package_type_name ?? '—'}</td>
                <td style={{ padding: '10px 10px' }}>{s.abv_target ? `${s.abv_target}%` : '—'}</td>
                <td style={{ padding: '10px 10px' }}><StatusBadge status={s.status} /></td>
                <td style={{ padding: '10px 10px', color: 'var(--text-muted)' }}>Rev.{s.revision_number}</td>
                <td style={{ padding: '10px 10px', textAlign: 'right' }}>
                  <span style={{ color: 'var(--pld-green)', fontWeight: 600, fontSize: 16 }}>›</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {createModal && (
        <SpecFormModal brands={brands} clients={clients}
          spiritCategories={spiritCategories} packageTypes={packageTypes}
          onClose={() => setCreateModal(false)}
          onSaved={() => { setCreateModal(false); refetch(); }} />
      )}
    </div>
  );
}

// ── Package types tab ─────────────────────────────────────────────────────────

function PackageTypeFormModal({ pkg, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:                pkg?.name ?? '',
    container_type:      pkg?.container_type ?? 'bottle',
    size_cl:             pkg?.size_cl ?? '',
    bottle_model_ref:    pkg?.bottle_model_ref ?? '',
    bottle_manufacturer: pkg?.bottle_manufacturer ?? '',
    closure_ref:         pkg?.closure_ref ?? '',
    closure_type:        pkg?.closure_type ?? '',
    units_per_case:      pkg?.units_per_case ?? 6,
    notes:               pkg?.notes ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setSaving(true); setErr('');
    const body = { ...form, size_cl: Number(form.size_cl), units_per_case: Number(form.units_per_case) };
    try {
      if (pkg) {
        await apiFetch(`/product-specs/package-types/${pkg.id}`, { method: 'PATCH', body });
      } else {
        await apiFetch('/product-specs/package-types', { method: 'POST', body });
      }
      onSaved();
    } catch (ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  return (
    <Modal title={pkg ? 'Edit Package Type' : 'New Package Type'} onClose={onClose} width={520}>
      <form onSubmit={submit}>
        <Field label="Display Name" required>
          <Input value={form.name} onChange={set('name')} required
            placeholder="e.g. 70cl Estal Philos Bold" />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
          <Field label="Container Type" required>
            <SelectEl value={form.container_type} onChange={set('container_type')}>
              {['bottle','keg','can','miniature','pouch','other'].map(v =>
                <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>)}
            </SelectEl>
          </Field>
          <Field label="Size (cl)" required>
            <Input type="number" step="0.01" value={form.size_cl} onChange={set('size_cl')} required
              placeholder="70" />
          </Field>
          <Field label="Bottle Model Ref">
            <Input value={form.bottle_model_ref} onChange={set('bottle_model_ref')} placeholder="Philos Bold" />
          </Field>
          <Field label="Bottle Manufacturer">
            <Input value={form.bottle_manufacturer} onChange={set('bottle_manufacturer')} placeholder="Estal" />
          </Field>
          <Field label="Closure Ref">
            <Input value={form.closure_ref} onChange={set('closure_ref')} placeholder="Tapered corkcoal 18.5mm" />
          </Field>
          <Field label="Closure Type">
            <SelectEl value={form.closure_type} onChange={set('closure_type')}>
              <option value="">Select…</option>
              {['cork','screw','crown','wax','other'].map(v =>
                <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>)}
            </SelectEl>
          </Field>
          <Field label="Units Per Case">
            <Input type="number" value={form.units_per_case} onChange={set('units_per_case')} min={1} />
          </Field>
        </div>
        <Field label="Notes (dimensions etc.)">
          <Textarea value={form.notes} onChange={set('notes')} rows={2} />
        </Field>
        {err && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 12 }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving…' : pkg ? 'Save' : 'Create'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function PackageTypesTab() {
  const { data, loading, refetch } = useApi('/product-specs/package-types', []);
  const pkgs = Array.isArray(data) ? data : [];
  const [modal, setModal] = useState(null);

  const CONTAINER_ICONS = { bottle: '🍾', keg: '🛢️', can: '🥤', miniature: '🥃', other: '📦' };

  return (
    <div>
      <SectionHeader label={`Package Types (${pkgs.length})`}
        action={<Btn size="sm" onClick={() => setModal({})}>+ New Package Type</Btn>} />

      {loading ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 20, textAlign: 'center' }}>Loading…</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border)' }}>
              {['', 'Name', 'Size', 'Bottle Model', 'Closure', 'Per Case', ''].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pkgs.map(p => (
              <tr key={p.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '10px 10px', fontSize: 18 }}>{CONTAINER_ICONS[p.container_type] ?? '📦'}</td>
                <td style={{ padding: '10px 10px', fontWeight: 600 }}>{p.name}</td>
                <td style={{ padding: '10px 10px' }}>{p.size_cl}cl</td>
                <td style={{ padding: '10px 10px', color: 'var(--text-muted)' }}>
                  {[p.bottle_manufacturer, p.bottle_model_ref].filter(Boolean).join(' ') || '—'}
                </td>
                <td style={{ padding: '10px 10px', color: 'var(--text-muted)' }}>{p.closure_ref || '—'}</td>
                <td style={{ padding: '10px 10px' }}>{p.units_per_case}</td>
                <td style={{ padding: '10px 10px', textAlign: 'right' }}>
                  <Btn size="sm" variant="ghost" onClick={() => setModal({ pkg: p })}>Edit</Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {modal && (
        <PackageTypeFormModal pkg={modal.pkg}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); refetch(); }} />
      )}
    </div>
  );
}

// ── Spirit categories tab ─────────────────────────────────────────────────────

function SpiritCategoryFormModal({ cat, onClose, onSaved }) {
  const [form, setForm] = useState({ name: cat?.name ?? '', description: cat?.description ?? '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setSaving(true); setErr('');
    try {
      if (cat) {
        await apiFetch(`/product-specs/spirit-categories/${cat.id}`, { method: 'PATCH', body: form });
      } else {
        await apiFetch('/product-specs/spirit-categories', { method: 'POST', body: form });
      }
      onSaved();
    } catch (ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  return (
    <Modal title={cat ? 'Edit Spirit Category' : 'New Spirit Category'} onClose={onClose} width={420}>
      <form onSubmit={submit}>
        <Field label="Category Name" required>
          <Input value={form.name} onChange={set('name')} required placeholder="e.g. London Dry Gin" />
        </Field>
        <Field label="Description">
          <Textarea value={form.description} onChange={set('description')} rows={2} />
        </Field>
        {err && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 12 }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving…' : cat ? 'Save' : 'Create'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function SpiritCategoriesTab() {
  const { data, loading, refetch } = useApi('/product-specs/spirit-categories', []);
  const cats = Array.isArray(data) ? data : [];
  const [modal, setModal] = useState(null);

  return (
    <div>
      <SectionHeader label={`Spirit Categories (${cats.length})`}
        action={<Btn size="sm" onClick={() => setModal({})}>+ New Category</Btn>} />

      {loading ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 20, textAlign: 'center' }}>Loading…</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border)' }}>
              {['Name', 'Description', ''].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cats.map(c => (
              <tr key={c.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '10px 12px', fontWeight: 600 }}>{c.name}</td>
                <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{c.description || '—'}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                  <Btn size="sm" variant="ghost" onClick={() => setModal({ cat: c })}>Edit</Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {modal && (
        <SpiritCategoryFormModal cat={modal.cat}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); refetch(); }} />
      )}
    </div>
  );
}

// ── Specs tab ─────────────────────────────────────────────────────────────────

function SpecsTab() {
  const { data: brandsData }    = useApi('/clients/brands', []);
  const { data: clientsData }   = useApi('/clients', []);
  const { data: catsData }      = useApi('/product-specs/spirit-categories', []);
  const { data: pkgsData }      = useApi('/product-specs/package-types', []);

  const brands          = Array.isArray(brandsData) ? brandsData : [];
  const clients         = Array.isArray(clientsData?.data) ? clientsData.data : Array.isArray(clientsData) ? clientsData : [];
  const spiritCategories = Array.isArray(catsData) ? catsData : [];
  const packageTypes    = Array.isArray(pkgsData) ? pkgsData : [];

  const [selectedId, setSelectedId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  if (selectedId) {
    return (
      <SpecDetail
        specId={selectedId}
        brands={brands} clients={clients}
        spiritCategories={spiritCategories} packageTypes={packageTypes}
        onBack={() => setSelectedId(null)}
        onRefresh={() => setRefreshKey(k => k + 1)} />
    );
  }

  return (
    <SpecList
      key={refreshKey}
      brands={brands} clients={clients}
      spiritCategories={spiritCategories} packageTypes={packageTypes}
      onSelect={setSelectedId} />
  );
}

// ── Default export ────────────────────────────────────────────────────────────

export default function ProductSpecs() {
  const [tab, setTab] = useState('specs');

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto' }}>
      <TabBar
        tabs={[
          { key: 'specs',      label: 'Specifications' },
          { key: 'packages',   label: 'Package Types' },
          { key: 'categories', label: 'Spirit Categories' },
        ]}
        active={tab} onChange={setTab} />

      {tab === 'specs'      && <SpecsTab />}
      {tab === 'packages'   && <PackageTypesTab />}
      {tab === 'categories' && <SpiritCategoriesTab />}
    </div>
  );
}
